import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { PUBLISH_TINYCMS_PAGES, RUN_TINYCMS_PAGE_COPY_EDIT } from '../../graphql/mutations';
import { useAccount } from '../../context/AccountContext';
import type { TodayGroup, TodayWaiting } from './todayData';

/** One thing in a bundle, as Today lists it. */
export interface BundleItem {
  kind: 'page' | 'form' | 'site';
  id: string;
  title: string | null;
  workingHash: string;
  copyEditPending?: boolean;
}

export type TogetherResult = { ok: true } | { ok: false; message: string };

/** The lists that show what's live, refreshed after a publish. */
const CONTENT_LISTS = [
  'GetTinycmsArticles',
  'GetTinycmsPages',
  'GetTinycmsForms',
  'GetTinycmsSite',
  // Today's queue and the menu's count.
  'GetTinycmsToday',
];

/** A Today group's members, site first, in the shape publishTogether takes. */
export function bundleItems(group: TodayGroup, waiting: TodayWaiting[]): BundleItem[] {
  const byId = new Map(waiting.map((w) => [`${w.kind}:${w.id}`, w]));
  const keys = [
    ...(group.site ? waiting.filter((w) => w.kind === 'site').map((w) => `site:${w.id}`) : []),
    ...group.pages.map((id) => `page:${id}`),
    ...group.forms.map((id) => `form:${id}`),
  ];
  return keys
    .map((k) => byId.get(k))
    .filter((w): w is TodayWaiting => Boolean(w))
    .map((w) => ({
      kind: w.kind as BundleItem['kind'],
      id: w.id,
      title: w.title,
      workingHash: w.workingHash,
      copyEditPending: w.copyEditPending,
    }));
}

/** "First fix: US English spelling and 4 more." while the bundle can't go live yet. */
export function bundleBlockedLine(group: TodayGroup | undefined): string | null {
  if (!group?.checks.blocking) return null;
  const first = (group.checks.first || 'a check').replace(/\.$/, '');
  const more = group.checks.more ? ` and ${group.checks.more} more` : '';
  return `First fix: ${first}${more}.`;
}

const quoted = (i: BundleItem) =>
  i.kind === 'site' ? 'the site’s look and navigation' : `“${i.title || 'Untitled'}”`;

/**
 * Publish a bundle in one approval (backlog bfbfa5d7): the site's look and
 * navigation, pages and forms that link to each other go live together, or
 * none do. Pages the account wants copy-edited and that haven't been get the
 * check first, so approving is one click; if it finds something to fix,
 * nothing is published and the person is told where. With an override reason
 * (Approve anyway, Skip copy edit) they go live as they are: no copy edit runs.
 */
export function usePublishTogether() {
  const { accountId } = useAccount();
  const [runCopyEdit] = useMutation(RUN_TINYCMS_PAGE_COPY_EDIT);
  const [publishPages] = useMutation(PUBLISH_TINYCMS_PAGES);
  const [step, setStep] = useState<'checking' | 'publishing' | null>(null);

  const publishTogether = useCallback(
    async (items: BundleItem[], overrideReason?: string): Promise<TogetherResult> => {
      try {
        const unchecked = items.filter((i) => i.kind === 'page' && i.copyEditPending);
        if (unchecked.length && !overrideReason) {
          setStep('checking');
          const results = await Promise.all(
            unchecked.map((i) =>
              runCopyEdit({ variables: { accountId, id: i.id } }).then(
                ({ data }) => [i, data?.runTinycmsPageCopyEdit] as const
              )
            )
          );
          const toFix = results.filter(([, r]) => (r?.blocking || 0) > 0);
          if (toFix.length) {
            const n = toFix.reduce((sum, [, r]) => sum + (r?.blocking || 0), 0);
            return {
              ok: false,
              message: `Nothing was published: the wording check found ${n} thing${
                n === 1 ? '' : 's'
              } to fix on ${toFix.map(([i]) => quoted(i)).join(', ')}.`,
            };
          }
          const changed = results.find(([, r]) => r && r.stored === false);
          if (changed) {
            return {
              ok: false,
              message: `Nothing was published: ${quoted(changed[0])} changed while it was checked. Try again.`,
            };
          }
        }
        setStep('publishing');
        const site = items.find((i) => i.kind === 'site');
        const { data } = await publishPages({
          variables: {
            accountId,
            pages: items
              .filter((i) => i.kind === 'page')
              .map((i) => ({ pageId: i.id, contentHash: i.workingHash })),
            forms: items
              .filter((i) => i.kind === 'form')
              .map((i) => ({ formId: i.id, contentHash: i.workingHash })),
            ...(site ? { site: { contentHash: site.workingHash } } : {}),
            ...(overrideReason ? { overrideReason } : {}),
          },
          refetchQueries: CONTENT_LISTS,
        });
        const r = data?.publishTinycmsPages;
        if (r?.ok) return { ok: true };
        const bad = (r?.items || []).find((i: any) => i.ok === false);
        const who = bad
          ? items.find((i) =>
              bad.kind === 'site' ? i.kind === 'site' : i.id === (bad.pageId || bad.formId)
            )
          : undefined;
        const n = (bad?.findings || []).filter((f: any) => f.severity === 'blocking').length;
        return {
          ok: false,
          message:
            who && bad?.refused === 'blocking_findings'
              ? `Nothing was published: ${quoted(who)} has ${
                  n ? `${n} thing${n === 1 ? '' : 's'}` : 'something'
                } to fix first.`
              : `Nothing was published: ${who ? `${quoted(who)}: ` : ''}${
                  bad?.message || r?.message || 'refused'
                }`,
        };
      } catch (err) {
        return {
          ok: false,
          message: `Nothing was published: ${err instanceof Error ? err.message : 'something went wrong'}`,
        };
      } finally {
        setStep(null);
      }
    },
    [accountId, runCopyEdit, publishPages]
  );

  return { publishTogether, step };
}
